import { debounce, getEditorNamespace } from 'ckeditor4-integrations-common'

import {
  defineComponent,
  h,
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
  ref,
} from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-button :ghost=false :type="'secondary'">Click Me</ak-button>
   */
  name: 'AkTextEditor',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'classic',
      validator: (type) => ['classic', 'inline'].includes(type),
    },
    editorUrl: {
      type: String,
      default: 'https://cdn.ckeditor.com/4.15.0/standard-all/ckeditor.js',
    },
    config: {
      type: Object,
      default: () => {},
    },
    tagName: {
      type: String,
      default: 'textarea',
    },
    readOnly: {
      type: Boolean,
      default: null, // Use null as the default value, so `config.readOnly` can take precedence.
    },
    throttle: {
      type: Number,
      default: 80,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue', 'focus', 'blur', 'ready'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const element = ref(null)
    const config = { ...props.config, readOnly: props.readOnly } || {}
    const method = props.type === 'inline' ? 'inline' : 'replace'
    let editor = null

    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    const setUpEditorEvents = () => {
      editor.on(
        'change',
        debounce(() => {
          const data = editor.getData()
          if (computedValue.value !== data) {
            computedValue.value = data
          }
        }, props.throttle)
      )
      editor.on('focus', (evt) => {
        emit('focus', evt, editor)
      })
      editor.on('blur', (evt) => {
        emit('blur', evt, editor)
      })
    }

    const callback = (data) => {
      return () => {
        setUpEditorEvents()
        const newData = editor.getData()
        if (data !== newData) {
          computedValue.value = newData
          emit('ready', editor)
        } else {
          emit('ready', editor)
        }
        editor.fire('unlockSnapshot')
      }
    }

    const isReady = () => {
      editor.fire('lockSnapshot')
      editor.setData(computedValue.value, {
        callback: callback(computedValue.value),
      })
    }

    const setupEditor = () => {
      const el = element.value.firstElementChild
      editor = CKEDITOR[method](el, config)
      editor.on('instanceReady', isReady)
    }

    onMounted(() => {
      getEditorNamespace(props.editorUrl).then(setupEditor)
    })

    onBeforeUnmount(() => {
      if (editor) {
        editor.destroy()
      }
    })

    watch(
      () => props.readOnly,
      (newVal) => {
        if (editor) {
          editor.setReadOnly(newVal)
        }
      }
    )

    return () => h('div', { ref: element }, [h(props.tagName)])
  },
})
